import {inject, Injectable} from "@angular/core";
import {OrgRepo} from "../repositories/api/org.repo";
import {map} from "rxjs/operators";
import {Additionals} from "../repositories/item/additionals";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {OrgDto} from "../repositories/dto/org.dto";
import {SettingDto} from "../repositories/dto/setting.dto";
import {AddressDto} from "../repositories/dto/address.dto";
import {PhoneDto} from "../repositories/dto/phone.dto";
import {BankAddressDto} from "../repositories/dto/bank-address.dto";
import {VoidDto} from "../repositories/dto/void.dto";
import {RepoProvider} from "../repositories/repo.provider";

@Injectable({providedIn: 'root'})
export class OrgService {

  private readonly orgRepo: OrgRepo = inject(RepoProvider).getRepo(OrgRepo);

  private currentOrg = new BehaviorSubject<OrgDto | undefined>(undefined);
  private orgChanged = new Subject<string>();

  public getById(id: string, add?: Additionals): Observable<OrgDto> {
    return this.orgRepo.readById(id, add).pipe(map((w) => w.getFirstData()));
  }

  public getCurrentOrg(): Observable<OrgDto | undefined> {
    return this.currentOrg.asObservable();
  }

  public setCurrentOrg(org: OrgDto | undefined): void {
    this.currentOrg.next(org);
  }

  public updateSettings(org: OrgDto, settings: SettingDto[]): Observable<SettingDto[]> {
    return this.orgRepo.updateSettings(org.id, settings)
      .pipe(map((w) => w.getDatas()));
  }

  public updateOrg(org: OrgDto): Observable<OrgDto> {
    return this.orgRepo.update(org).pipe(map((w) => w.getFirstData()));
  }

  // if the logo is changed, then org must be updated in the state
  public uploadLogo(org: OrgDto, logoFile: File): Observable<VoidDto> {
    return this.orgRepo.uploadLogo(org, logoFile).pipe(map((w) => {
      this.orgChanged.next(org.id);
      return w.getFirstData();
    }));
  }

  // if the logo is changed, then org must be updated in the state
  public deleteLogo(org: OrgDto): Observable<VoidDto> {
    return this.orgRepo.deleteLogo(org).pipe(map((w) => {
      this.orgChanged.next(org.id);
      return w.getFirstData();
    }));
  }

  public onOrgChanged(): Observable<string> {
    return this.orgChanged.asObservable();
  }

  public getAddresses(org: OrgDto): Observable<AddressDto[]> {
    return this.orgRepo.readAddresses(org).pipe(map((w) => w.getDatas()));
  }

  public updateAddress(org: OrgDto, address: AddressDto): Observable<AddressDto> {
    return this.orgRepo.updateAddress(org, address)
      .pipe(map((w) => w.getFirstData()));
  }

  public deleteAddress(org: OrgDto, address: AddressDto): Observable<VoidDto> {
    return this.orgRepo.deleteAddress(org, address.id)
      .pipe(map((w) => w.getFirstData()));
  }

  public getPhones(org: OrgDto,): Observable<PhoneDto[]> {
    return this.orgRepo.readPhones(org)
      .pipe(map((w) => w.getDatas()));
  }

  public updatePhone(org: OrgDto, phone: PhoneDto): Observable<PhoneDto> {
    return this.orgRepo.updatePhone(org, phone)
      .pipe(map((w) => w.getFirstData()));
  }

  public deletePhone(org: OrgDto, phone: PhoneDto): Observable<VoidDto> {
    return this.orgRepo.deletePhone(org, phone.id)
      .pipe(map((w) => w.getFirstData()));
  }

  public getBankAddresses(org: OrgDto): Observable<BankAddressDto[]> {
    return this.orgRepo.readBankAddresses(org)
      .pipe(map((w) => w.getDatas()));
  }

  public updateBankAddress(org: OrgDto, bankAddress: BankAddressDto): Observable<BankAddressDto> {
    return this.orgRepo.updateBankAddress(org, bankAddress)
      .pipe(map((w) => w.getFirstData()));
  }

  public deleteBankAddress(org: OrgDto, bankAddress: BankAddressDto): Observable<VoidDto> {
    return this.orgRepo.deleteBankAddress(org, bankAddress.id)
      .pipe(map((w) => w.getFirstData()));
  }
}
